import React, { useState, useEffect } from 'react';
import { Heading } from '../Components/Catalyst/heading';
import { useLocation, useNavigate } from 'react-router-dom';
import { ViolationReturn } from '../clients/rust-api-client';
import { memberViolationService } from '../clients/ApiClients';
import { ErrorAlert } from '../Components/Custom/ErrorAlert';
import LoadingSpinner from '../LoadingSpinner';
import { apiErrorMessage } from '../utils/apiErrors';
import { ViolationFilterBar } from './linear/ViolationFilterBar';
import { GroupedViolationList } from './linear/GroupedViolationList';
import { saveListContext } from './linear/listContext';
import {
  EMPTY_FILTERS,
  ViolationFilters,
  applyFilters,
  groupByStatus,
} from './linear/violationMeta';

interface ViolationListProps {
  title?: string | null;
}

/**
 * Violations cited against the member's own homes, as the same grouped
 * list managers see (#603 step 2) — status groups, filter chips, dense
 * rows — so both sides of a violation look at it the same way.
 *
 * The endpoint is scoped by membership on the server — there is no
 * `memberId` to pass, and there never should be one: the caller's token
 * decides what comes back.
 */
const ViolationList: React.FC<ViolationListProps> = ({ title }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [violations, setViolations] = useState<ViolationReturn[]>([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState<ViolationFilters>(EMPTY_FILTERS);
  const [fetchError, setFetchError] = useState<string | null>(null);
  // Bumped by "Try again" to re-run the fetch below.
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    const fetchViolations = async () => {
      try {
        setLoading(true);
        setFetchError(null);
        const response = await memberViolationService.listMyViolations();
        setViolations(response.data.violations);
      } catch (err) {
        console.error('Error fetching violations:', err);
        setFetchError(apiErrorMessage(err, "Couldn't load violations."));
      } finally {
        setLoading(false);
      }
    };

    fetchViolations();
  }, [reloadKey]);

  const visibleViolations = applyFilters(violations, filters);
  const groups = groupByStatus(visibleViolations);

  const handleRowClick = (violation: ViolationReturn) => {
    saveListContext({
      ids: groups.flatMap((g) => g.violations.map((v) => v.id)),
      from: location.pathname,
    });
    navigate(`/violation/${violation.id}`);
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <>
      <div className="mb-3">
        <Heading level={4}>{title || 'Your Violations'}</Heading>
      </div>

      <ErrorAlert
        className="mb-3"
        onRetry={() => setReloadKey((k) => k + 1)}
        onDismiss={() => setFetchError(null)}
      >
        {fetchError}
      </ErrorAlert>

      <div className="overflow-hidden rounded-lg border border-line bg-raised">
        <ViolationFilterBar
          filters={filters}
          onChange={setFilters}
          shown={visibleViolations.length}
          total={violations.length}
        />
        <GroupedViolationList
          groups={groups}
          onOpen={handleRowClick}
          emptyMessage={
            fetchError
              ? "Couldn't load violations."
              : violations.length === 0
              ? 'No violations on your homes.'
              : 'No violations match these filters.'
          }
        />
      </div>
    </>
  );
};

export default ViolationList;
